import React, { useContext, useEffect } from "react";
import { Carousel, Checkbox, Col, Radio, Row, Select, Spin, Tooltip } from "antd";
import { FormItem } from "./AddOptionHelperComponents";
import { FIELDS_LABEL } from "constants/addOption";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { toast } from "helpers/generalHelper";
import { GlobalContext } from "contexts/GlobalContext";

const BESPOKE_PERIOD_OPTIONS = [
  { value: 1, label: "Month" },
  { value: 2, label: "Quarter" },
  { value: 3, label: "Season" },
  { value: 4, label: "Calendar" },
];

function Tenors({
  fetchedData,
  isBespokeChecked,
  setIsBespokeChecked,
  tenorDataSelectedButtons,
  setTenorDataSelectedButtons,
  isCSO = false,
  bespokePeriodId = 1,
  setBespokePeriodId,
  bespokePeriods = [],
  bespokeTenorsLoading = false,
}) {
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const renderBespokeCell = (cell, keyPrefix) => {
    const cellId = cell.style_period_tenor_month_id ?? cell.style_period_tenor_id;
    const checkboxJSX = (
      <Checkbox
        className="tenor-date"
        key={`${keyPrefix}${cellId}`}
        value={cellId}
      >
        {cell.title}
      </Checkbox>
    );
    return cell.title.length > 7 ? (
      <Tooltip
        title={cell.title}
        placement="top"
        key={`${keyPrefix}${cellId}-tooltip`}
      >
        {checkboxJSX}
      </Tooltip>
    ) : (
      checkboxJSX
    );
  };

  const showBespokeTenorPicker = () => {
    if (bespokeTenorsLoading) {
      return (
        <div
          className="tenors-date-carousel"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 130 }}
        >
          <Spin />
        </div>
      );
    }

    const bespokeData = fetchedData?.[FIELDS_LABEL.BESPOKE_TENOR.key];
    const groups = bespokeData?.groups;

    if (!Array.isArray(groups) || groups.length === 0) return null;

    // Month: Carousel sliding per year with year header
    if (bespokePeriodId === 1) {
      return (
        <Carousel
          className="tenors-date-carousel"
          arrows
          dots={false}
          swipeToSlide
          rows={1}
          prevArrow={<LeftOutlined />}
          nextArrow={<RightOutlined />}
        >
          {groups.map((group) => (
            <React.Fragment key={group.year}>
              <div className="bespoke-tenor-title">{group.year}</div>
              {group.rows.map((row, rowIdx) => (
                <div key={rowIdx} id="bespoke-checkbox-list">
                  {row.map((cell) =>
                    renderBespokeCell(cell, `m-${group.year}-${rowIdx}-`)
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}
        </Carousel>
      );
    }

    // Qtr / Season / Half / Cal:
    // Flatten every returned cell into a simple grid without padding to 12 boxes.
    const COL_SIZE = 3;
    const allCells = groups.flatMap((g) => g.rows.flat());

    const flatRows = [];
    for (let i = 0; i < allCells.length; i += COL_SIZE) {
      flatRows.push(allCells.slice(i, i + COL_SIZE));
    }

    return (
      <div>
        <div className="bespoke-tenor-title" aria-hidden style={{ visibility: "hidden" }}>
          –
        </div>
        {flatRows.map((row, rowIdx) => (
          <div key={rowIdx} id="bespoke-checkbox-list">
            {row.map((cell, cellIdx) =>
              renderBespokeCell(cell, `nm-${rowIdx}-${cellIdx}-`)
            )}
          </div>
        ))}
      </div>
    );
  };

  const showTenorDatePicker = (parentIndex, useCheckboxes = false) => {
    const constraintsTenorNumber =
      fetchedData?.[FIELDS_LABEL.CONSTRAINTS.key]?.tenors;
    if (
      !fetchedData?.[FIELDS_LABEL.COMMODITY.key] ||
      !fetchedData?.[FIELDS_LABEL.TENOR.key] ||
      !constraintsTenorNumber
    )
      return;

    return Object.values(fetchedData[FIELDS_LABEL.TENOR.key]).map(
      (item, index) => {
        return (
          <Carousel
            key={JSON.stringify(item[index])}
            className="tenors-date-carousel"
            arrows
            dots={false}
            swipeToSlide
            rows={1}
            prevArrow={<LeftOutlined />}
            nextArrow={<RightOutlined />}
          >
            {item.map((nestedElement) => {
              return (
                <div
                  key={nestedElement.id}
                  id={useCheckboxes ? "bespoke-checkbox-list" : undefined}
                >
                  {nestedElement.map((nestedElement2) => {
                    if (useCheckboxes) {
                      const checkboxButtonJSX = (
                        <Checkbox
                          className="tenor-date"
                          key={`${nestedElement2.title}${nestedElement2.id}`}
                          value={nestedElement2.id}
                        >
                          {nestedElement2.title}
                        </Checkbox>
                      );

                      return nestedElement2.title.length > 7 ? (
                        <Tooltip
                          title={nestedElement2.title}
                          placement="top"
                          key={`${nestedElement2.title}${nestedElement2.id}-tooltip`}
                        >
                          {checkboxButtonJSX}
                        </Tooltip>
                      ) : (
                        checkboxButtonJSX
                      );
                    } else {
                      const radioButtonJSX = (
                        <Radio.Button
                          onClick={() =>
                            setTenorDataSelectedButtons((prev) => ({
                              ...prev,
                              [parentIndex]: nestedElement2.id,
                            }))
                          }
                          className="tenor-date"
                          key={`${nestedElement2.title}${nestedElement2.id}`}
                          value={nestedElement2.id}
                        >
                          {nestedElement2.title}
                        </Radio.Button>
                      );

                      return nestedElement2.title.length > 7 ? (
                        <Tooltip title={nestedElement2.title} placement="top">
                          {radioButtonJSX}
                        </Tooltip>
                      ) : (
                        radioButtonJSX
                      );
                    }
                  })}
                </div>
              );
            })}
          </Carousel>
        );
      }
    );
  };

  const showBespoke =
    fetchedData[FIELDS_LABEL.CONSTRAINTS.key]?.["show_bespoke_checkbox"];

  useEffect(() => {
    if (isCSO && fetchedData[FIELDS_LABEL.CONSTRAINTS.key]) {
      const tenorCount =
        fetchedData[FIELDS_LABEL.CONSTRAINTS.key][FIELDS_LABEL.TENOR.key];
      if (tenorCount) {
        const updates = {};
        let hasUpdates = false;

        for (let i = 0; i < tenorCount; i++) {
          if (
            !isBespokeChecked[i] &&
            !Array.isArray(tenorDataSelectedButtons[i])
          ) {
            updates[i] = [];
            hasUpdates = true;
          }
        }

        if (hasUpdates) {
          setTenorDataSelectedButtons((prev) => ({
            ...prev,
            ...updates,
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCSO, fetchedData[FIELDS_LABEL.CONSTRAINTS.key]]);

  return (
    <Col span={24}>
      <FormItem label={FIELDS_LABEL.TENOR.label}>
        <Row gutter={[60, 0]} className="horizontal-scroll__container">
          {fetchedData[FIELDS_LABEL.CONSTRAINTS.key] &&
            Array.from(
              Array(
                fetchedData[FIELDS_LABEL.CONSTRAINTS.key][
                  FIELDS_LABEL.TENOR.key
                ]
              ),
              (element, parentIndex) => {
                if (!(parentIndex in isBespokeChecked)) {
                  setIsBespokeChecked((prev) => ({
                    ...prev,
                    [parentIndex]: false,
                  }));
                }

                const shouldUseMultipleSelection =
                  isBespokeChecked[parentIndex] || isCSO;

                if (shouldUseMultipleSelection && isBespokeChecked[parentIndex]) {
                  return (
                    <Col sm={12} md={8} key={parentIndex}>
                      <Row gutter={[0, 0]}>
                        <Col span={24}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 90, marginBottom: 8 }}>
                            {showBespoke && (
                              <label style={{ display: "inline-flex", alignItems: "center", gap: 90, whiteSpace: "nowrap" }}>
                                <span style={{ fontSize: 14 }}>{FIELDS_LABEL.BESPOKE.label}:</span>
                                <Checkbox
                                  checked={isBespokeChecked[parentIndex]}
                                  onChange={(e) =>
                                    setIsBespokeChecked((prev) => ({
                                      ...prev,
                                      [parentIndex]: e.target.checked,
                                    }))
                                  }
                                />
                              </label>
                            )}
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: 14 }}>Period:</span>
                              <Select
                                  value={bespokePeriodId}
                                  options={BESPOKE_PERIOD_OPTIONS}
                                  style={{ width: 110 }}
                                  popupClassName={dark_Theme ? "dropdown-dark" : ""}
                                  onChange={(val) => {
                                    setBespokePeriodId(val);
                                    setTenorDataSelectedButtons((prev) => ({
                                      ...prev,
                                      [parentIndex]: [],
                                    }));
                                  }}
                                />
                            </label>
                          </div>
                        </Col>
                        <Col span={24}>
                          <Checkbox.Group
                            className="tenor__container"
                            buttonStyle="solid"
                            key={parentIndex}
                            value={
                              Array.isArray(tenorDataSelectedButtons[parentIndex])
                                ? tenorDataSelectedButtons[parentIndex]
                                : []
                            }
                            onChange={(e) =>
                              setTenorDataSelectedButtons((prev) => ({
                                ...prev,
                                [parentIndex]: e,
                              }))
                            }
                          >
                            {showBespokeTenorPicker()}
                          </Checkbox.Group>
                        </Col>
                      </Row>
                    </Col>
                  );
                } else if (shouldUseMultipleSelection && isCSO) {
                  return (
                    <Col sm={12} md={8} key={parentIndex}>
                      <Row gutter={[0, 0]}>
                        <Col span={24}>
                          <Checkbox.Group
                            className="tenor__container"
                            buttonStyle="solid"
                            style={{ marginTop: 30 }}
                            key={parentIndex}
                            value={
                              Array.isArray(tenorDataSelectedButtons[parentIndex])
                                ? tenorDataSelectedButtons[parentIndex]
                                : []
                            }
                            onChange={(selected) => {
                              if (selected.length > 2) {
                                const tenorNumber = parentIndex + 1;
                                toast(
                                  `CSO tenor ${tenorNumber} must have only 2 months.`,
                                  "warning"
                                );
                                return;
                              }

                              setTenorDataSelectedButtons((prev) => ({
                                ...prev,
                                [parentIndex]: selected,
                              }));
                            }}
                          >
                            {showBespokeTenorPicker()}
                          </Checkbox.Group>
                        </Col>
                      </Row>
                    </Col>
                  );
                } else {
                  return (
                    <Col sm={12} md={8} key={parentIndex}>
                      <Row gutter={[0, 0]}>
                        {showBespoke && (
                          <Col span={24}>
                            <FormItem label={FIELDS_LABEL.BESPOKE.label}>
                              <Checkbox
                                checked={isBespokeChecked[parentIndex]}
                                onChange={(e) =>
                                  setIsBespokeChecked((prev) => ({
                                    ...prev,
                                    [parentIndex]: e.target.checked,
                                  }))
                                }
                              />
                            </FormItem>
                          </Col>
                        )}
                        <Col span={24}>
                          <Radio.Group
                            className="tenor__container"
                            buttonStyle="solid"
                            style={{ marginTop: 30 }}
                            value={
                              Array.isArray(tenorDataSelectedButtons[parentIndex])
                                ? undefined
                                : tenorDataSelectedButtons[parentIndex]
                            }
                          >
                            {showTenorDatePicker(parentIndex, false)}
                          </Radio.Group>
                        </Col>
                      </Row>
                    </Col>
                  );
                }
              }
            )}
        </Row>
      </FormItem>
    </Col>
  );
}

export default Tenors;
